<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="(dialog = true), (editMode = false)"
      @updateSearch="(v) => (search = v)"
      headerText="Fee Types"
      @fileUploaded="initialise"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined rounded class="ft-sheet">
          <v-data-table
            :headers="headers"
            :search="search"
            :items="feeTypes"
            :custom-filter="customFilter"
            :loading="loading"
            loading-text="Loading fee types…"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            fixed-header
            class="secondary row-height-50 ft-table"
          >
            <template #[`item.name`]="{ item }">
              <span class="ft-name">{{ item.name }}</span>
            </template>

            <template #[`item.categories`]="{ item }">
              <v-chip
                v-for="category in item.categories"
                :key="category"
                x-small
                label
                outlined
                class="mr-1 my-1"
              >
                {{ category }}
              </v-chip>
              <span v-if="!item.categories || !item.categories.length" class="ft-untagged">
                Not tagged
              </span>
            </template>

            <template #[`item.is_self`]="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <div class="ft-check" v-on="on">
                    <v-simple-checkbox
                      :value="item.is_self"
                      :ripple="false"
                      color="primary"
                      @input="askToggle(item, 'is_self', $event)"
                    ></v-simple-checkbox>
                  </div>
                </template>
                <span>
                  {{ item.is_self ? "Available to" : "Not available to" }} self partners
                </span>
              </v-tooltip>
            </template>

            <template #[`item.is_business`]="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <div class="ft-check" v-on="on">
                    <v-simple-checkbox
                      :value="item.is_business"
                      :ripple="false"
                      color="primary"
                      @input="askToggle(item, 'is_business', $event)"
                    ></v-simple-checkbox>
                  </div>
                </template>
                <span>
                  {{ item.is_business ? "Available to" : "Not available to" }} business
                  partners
                </span>
              </v-tooltip>
            </template>

            <template #[`item.active`]="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-chip
                    small
                    label
                    outlined
                    class="ft-status"
                    :color="item.active ? 'success' : 'error'"
                    v-on="on"
                    @click="openConfirm(item)"
                  >
                    <v-icon x-small left>
                      {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ showActiveStatus(item.active) }}
                  </v-chip>
                </template>
                <span>Click to {{ item.active ? "deactivate" : "activate" }}</span>
              </v-tooltip>
            </template>

            <template #no-data>
              <div class="ft-empty">
                <v-icon size="40" color="grey lighten-1">mdi-cash-register</v-icon>
                <p class="ft-empty__title">No fee types yet</p>
                <p class="ft-empty__text">
                  Add a fee type to start configuring partner revenue splits.
                </p>
              </div>
            </template>

            <template #no-results>
              <div class="ft-empty">
                <v-icon size="40" color="grey lighten-1">mdi-magnify</v-icon>
                <p class="ft-empty__title">No matches for “{{ search }}”</p>
                <p class="ft-empty__text">Try a different name, code, or status.</p>
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <FeeTypeFormDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="createFeeType"
        @update="updateFeeType"
        :feeType="feeType"
        :edit="editMode"
      />
    </v-dialog>
    <ConfirmDialog
      v-if="showDialog"
      :show="showDialog"
      @delete="changeConfirmation"
      @close="showDialog = false"
      title="fee type"
      :name="currentItem.name"
      :active="currentItem.active"
    />
    <v-dialog v-model="showFlagDialog" width="450" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text"> Confirmation </v-card-title>
        <v-card-text class="mt-4">
          <div class="subtitle-2">
            Are you sure want to {{ pendingFlag.value ? "enable" : "disable" }}
             {{ pendingFlag.name }} for
             {{ flagLabel(pendingFlag.key) }}?
          </div>
        </v-card-text>
        <v-card-actions class="mt-n3 d-flex justify-end">
          <v-btn text color="green" :loading="saving" @click="confirmToggle">
            Yes
          </v-btn>
          <v-btn text color="red" :disabled="saving" @click="cancelToggle">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FeeTypeFormDialog from "@/components/fee_types/form.vue";
import searchBar from "@/components/searchBar.vue";
import { feeTypeHeaders } from "@/constants/headers";
import ConfirmDialog from "@/components/confirm.vue";

export default {
  components: {
    FeeTypeFormDialog,
    searchBar,
    ConfirmDialog,
  },
  data() {
    return {
      dialog: false,
      editMode: false,
      feeType: {},
      currentItem: {},
      showDialog: false,
      showFlagDialog: false,
      pendingFlag: { item: null, key: "", value: false, name: "" },
      saving: false,
      loading: false,
      feeTypes: [],
      search: "",
      headers: feeTypeHeaders,
    };
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Fee Types" }]);
    this.initialise();
  },
  methods: {
    initialise() {
      this.getFeeTypes();
    },
    async getFeeTypes() {
      this.loading = true;
      await this.$axios
        .get("fee_types")
        .then((res) => {
          this.feeTypes = res.data.data
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response?.data?.message || "Failed to fetch fee types",
            color: "error",
          });
        });
      this.loading = false;
    },
    async createFeeType(data) {
      await this.$axios
        .post("fee_types", { fee_type: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee type created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateFeeType(data) {
      await this.$axios
        .put("fee_types/" + data.id, { fee_type: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee type updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    changeConfirmation() {
      this.updateFeeType({
        id: this.currentItem.id,
        active: !this.currentItem.active,
      });
    },
    openConfirm(item) {
      this.showDialog = true;
      this.currentItem = { ...item };
    },
    flagLabel(key) {
      return key === "is_self" ? "Self Partner" : "Business Partner";
    },
    askToggle(item, key, value) {
      if (value === item[key]) return;
      this.pendingFlag = { item, key, value, name: item.name };
      this.showFlagDialog = true;
    },
    cancelToggle() {
      this.showFlagDialog = false;
      this.pendingFlag = { item: null, key: "", value: false, name: "" };
    },
    async confirmToggle() {
      const { item, key, value } = this.pendingFlag;
      if (!item) return;
      const previous = item[key];
      this.saving = true;
      this.$set(item, key, value);
      await this.$axios
        .put("fee_types/" + item.id, { fee_type: { id: item.id, [key]: value } })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee type updated successfully",
            color: "success",
          });
        })
        .catch(async (error) => {
          this.$set(item, key, previous);
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
      this.saving = false;
      this.cancelToggle();
    },
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.showDialog = false;
      this.currentItem = {};
      this.feeType = {};
    },
    showActiveStatus(value) {
      return value ? "Active" : "Inactive";
    },
    customFilter(value, search, item) {
      if (!search) return true;
      const activeStatus = this.showActiveStatus(item.active);
      return (
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase()) ||
        activeStatus.toLowerCase().includes(search.toLowerCase())
      );
    },
  },
};
</script>

<style scoped>
.ft-untagged {
  font-size: 12px;
  color: #b54708;
}

.ft-sheet {
  border-color: #e4e7ec !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.ft-name {
  font-weight: 500;
  color: #101828;
}

.ft-check {
  display: inline-flex;
}

.ft-status {
  font-weight: 600;
  cursor: pointer;
}

.ft-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 20px;
}

.ft-empty__title {
  margin: 12px 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}

.ft-empty__text {
  margin: 0;
  font-size: 13px;
  color: #475467;
}
</style>